<template>
	<div class="infoBlockTable">
		<v-data-table
				class="infoTable motivationTable"
				:headers="headers"
				:page.sync="page"
				:items="items"
				:sort-by.sync="sortBy"
				:sort-desc.sync="sortDesc"
				:items-per-page="getItemsPerPage"
				@page-count="pageCount = $event"
				hide-default-footer
				hide-default-header
				hide-default-body
				fixed-header
		>
			<template v-slot:header="{ props: { headers } }">
				<thead>
				<tr>
					<th
							v-for="(item, index) in headers"
							:class="[{'filter-active': filterClasses[item.valueFilter] || filterClassesActive(item.value)},
                                {'dense': item.dense}]"
							:key="index"
							class=""
							:id="'container-'+item.value"
					>
						<div class="filterIcons">
							<sort-btn
									v-if="item.sortable"
									:sortDesc="sortDesc"
									:sortBy="sortBy"
									:data-sort="item.value"
									@updateToggleOrder="toggleOrder"
							></sort-btn>
							<span class="headText wh_w"
							      :class="{'headText_sorted': getSortColor(sortBy, item.value), 'center': item.value === 'optional'}">
                                {{ item.text }}
                            </span>
							<filter-drop-down v-if="item.func" :item="item">
								<filter-table
										:item="item"
										:data-filter="filter[item.value]"
										:type-filter="item.model"
										:model-filter="item.value"
										:filter-update="filterUpdate"
										:is-disabled="filterPreloader"
										:isRefresh="isRefresh"
										:labelFilter="item.text"
										:uniqId="uniqId"
										@change-sort="changeSort"
										@create-refresh="createRefresh"
										@filter-created="filterCreated"
										@updateFilterListAll="onFilteredAll(item)"
								></filter-table>
							</filter-drop-down>
						</div>
					</th>
				</tr>
				</thead>
			</template>
			<template v-slot:body="{ items }">
				<tbody>
				<template v-for="(item, idx) in items">
					<table-tr
							:item="item"
							:key="item.id"
							:idx="idx"
							:checkClasses="checkClasses"
							:permissions="permissions"
							:filterClassesActive="filterClassesActive"
					></table-tr>
				</template>
				<tr class="tableBottom">
					<td>n: <span>{{ getUniqUnit }}</span></td>
					<td :key="i" v-for="i in 6"></td>
				</tr>
				</tbody>
			</template>
		</v-data-table>
		<pagination
				v-if="items.length"
				:length="items.length"
				:pageCount="pageCount"
				:itemsPerPage="itemsPerPage"
				:page="page"
				:isRefresh="isRefresh"
				:isRefreshPage="isRefreshPage"
				:pages="pages"
				@updatePagination="onPagination"
				@updateCountPagination="onCountPagination"
		></pagination>
	</div>
</template>

<script>
import { mapActions } from "vuex";
// components
import TableTr from "@/components/Fleet/WorkOrdersStatistics/MotivationProgram/MotivationInfo/TableTr/TableTr";
import FilterDropDown from "@/shared/FilterDropDown/FilterDropDown";
// shared
import FilterTable from "@/shared/FilterTable/FilterTable";
import Pagination from "@/shared/Pagination/Pagination";
import SortBtn from "@/shared/SortBtn/SortBtn";
// constants
import { OPTIONAL_KEYS } from "@/constants/constants";
import { motivationHeader } from "@/constants/defaultHeader";
// helpers
import getUniqId from "@/helper/getUniqId";
import debounce from 'lodash.debounce';
import { formatCurrency, formatDate } from "@/helper";

export default {
	name: "InfoTable",
	components: {
		TableTr,
		FilterTable,
		Pagination,
		SortBtn,
		FilterDropDown
	},
	props: {
		listItems: Array,
		filter: Object,
		workOrdersFilterDefault: Object,
		permissions: Object,
		isRefresh: String,
		currentDataInfo: Object,
		getTruckId: Number
	},
	computed: {
		headers() {
			return motivationHeader;
		},
		getUniqUnit() {
			return Array.from(new Set(this.listItems.map(item => item.truck_number))).length;
		},
		getItemsPerPage() {
			return this.itemsPerPage === 'All' ? 99999999 : this.itemsPerPage;
		},
		getTotal() {
			const total = this.listItems.reduce((acc, item) => acc += +item.total, 0);
			return formatCurrency(total);
		},
		items: {
			get() {
				return this.list;
			},
			set(arr) {
				this.list = arr;
			},
		},
		filterClasses: {
			get() {
				return this.checkClasses;
			},
			set(arr) {
				const obj = {};
				for (const i in arr) {
					obj[i] = arr[i].length;
				}
				this.checkClasses = obj;
			},
		},
	},
	watch: {
		listItems(val) {
			this.items = val;
		},
		async isRefresh(val, oldVal) {
			if (val !== oldVal) {
				await this.refreshFilters();
			}
		}
	},
	data() {
		return {
			pages: [ 10, 20, 50, 100, 200, 500 ],
			itemsPerPage: 10,
			page: 1,
			pageCount: 0,
			filterUpdate: false,
			sortBy: "id",
			sortDesc: true,
			menu: false,
			filtersMarker: {},
			filters: {},
			filtersList: {},
			list: this.listItems,
			checkClasses: {},
			filterPreloader: false,
			isRefreshPage: '',
			uniqId: { key: '', id: '' },
			isVisibleItems: false
		};
	},
	methods: {
		...mapActions([ 'onWorkOrdersFilter', 'onMotivationProgramFilter' ]),
		onFilteredAll(item) {
			this.filterCreated(item.value);
		},
		filterClassesActive(filterKey) {
			const { filter_request_date_empty, filter_check_out_empty, filter_arrival_date_empty } = this.filters || {};
			return (filterKey === "filter_request_date" && filter_request_date_empty)
					|| (filterKey === "filter_check_out" && filter_check_out_empty)
					|| (filterKey === "filter_arrival_date" && filter_arrival_date_empty);
		},
		onPagination(value) {
			this.page = value;
		},
		onCountPagination(value) {
			this.itemsPerPage = value;
		},
		async refreshFilters() {
			this.search = "";
			this.sortDesc = false;
			this.sortBy = "id";
			this.items = this.listItems;
			this.page = 1;

			if (Object.values(this.filterClasses)) {
				for (const i in this.filters) {
					const isOptional = OPTIONAL_KEYS.some(item => i.includes(item));
					this.filters[i] = isOptional ? -1 : [];
				}

				this.filters['filter_reviewed'] = 0;
				this.filterUpdate = true;
			}

			const obj = { date: formatDate(this.currentDate, 1), filters: {} };
			await this.onMotivationProgramFilter(obj);
		},
		createRefresh(model) {
			this.filtersMarker[model] = true;
			if (!Object.values(this.filtersMarker).includes(false)) {
				this.filterUpdate = false;
				for (const i in this.filtersMarker) {
					this.filtersMarker[i] = true;
				}
			}
			this.filterClasses = this.filters;
		},
		filterCreated(model) {
			this.filtersMarker[model] = false;
		},
		getSortColor(sortBy, value) {
			return sortBy === value || (sortBy && value.includes(sortBy));
		},
		toggleOrder(val) {
			const { valueFilter } = this.headers.find(item => item.value === val);
			this.sortBy = valueFilter;
			this.sortDesc = !this.sortDesc;
		},
		initWorkOrdersFilter: debounce(async function(values) {
			await this.onMotivationProgramFilter(values);
			this.isRefreshPage = getUniqId();
			this.filterPreloader = false;
		}, 800),
		changeSort({ key, value, model }) {
			switch (model) {
				case 'date':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key]['from'];
					break;
				case 'number':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key]['type'];
					break;
				case 'note':
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key].length;
					break;
				case 'options':
					this.filters[key] = Number(value);
					this.filterClasses[key] = !!this.filters[key].length;
					break;
				case 'checkbox':
					this.filters[key] = Number(value);
					break;
				case 'list':
					this.filters[key] = value.map(item => item);
					this.filterClasses[key] = !!this.filters[key].length;
					break;
				default:
					this.filters[key] = value;
					this.filterClasses[key] = !!this.filters[key].length;
			}

			this.filterPreloader = true;
			const values = {
				...this.filters,
				...this.currentDataInfo,
			};
			this.initWorkOrdersFilter(values);
		},
		async getFilterList() {
			for (let i = 0; i < this.headers.length; i++) {
				this.filtersMarker[this.headers[i].value] = true;
				this.$set(this.checkClasses, this.headers[i].value, false);
				const value = this.headers[i].value;

				if (value.includes('filter')) {
					this.filters[value] = [];
				}

				this.filtersList[value] = [];
			}
		}
	},
	created() {
		this.getFilterList();
	}
};
</script>

<style scoped lang="scss">
.filterListItems {
	padding: 0;

	.filterListItem {
		padding: 0;
	}
}

.dense {
	padding: 0 5px !important;
}

.wh_w {
	white-space: nowrap;
}

.logsSort {
	display: inline-flex;
	flex-direction: column;
	margin-right: 10px;
}

.filterIcons {
	display: flex;
	align-items: center;

	.headText {
		flex-grow: 2;
		color: #71CC82;

		&.center {
			text-align: center;
		}
	}
}

.infoBlockTable {
	padding: 10px 10px 0;
	background: #2B2B37;
	box-shadow: 2px 4px 10px rgba(0, 0, 0, 0.3);
	border-radius: 4px;

	tr {
		background: #2B2B37;

		th {
			border-right: 1px solid rgba(25, 188, 56, 0.5);
			border-bottom: 1px solid rgba(25, 188, 56, 0.5) !important;
			background: #2b2b37 !important;
			font-weight: 500;
			font-size: 14px;
			line-height: 20px;
			letter-spacing: 0.25px;
			color: #71CC82;
			white-space: nowrap;
		}

		th:last-child {
			border-right: none;
		}
	}
}

.tableBottom {
	position: sticky;
	bottom: 0;

	td {
		background: #1c1c2880;
		height: 30px !important;

		span {
			font-weight: 500;
			font-size: 14px;
			line-height: 20px;
			color: #19BC38;
		}
	}
}

.greenColor {
	color: #71CC82;
}
</style>

<style>
.infoTable.motivationTable .v-data-table__wrapper {
	max-height: 500px;
}
</style>
